/**
 * A read's answer, checked at the read for the shape the screen draws from (I-1162).
 *
 * Answered with an empty record, or a list where a record belongs, a screen used to draw defaults as
 * if they were the stored settings (email intake offered them to Save), or throw on a missing field
 * and take the page to the route error screen. Checked here, a wrong shape is a failed read like a
 * refused one: the screen says so with its Try again (`LoadFailureBanner`).
 */
export class UnreadableAnswerError extends Error {
  /** Which read, for the banner's technical details. */
  readonly reference: string;

  constructor(what: string) {
    super(`${what} came back in a shape this app cannot read.`);
    this.name = 'UnreadableAnswerError';
    this.reference = what;
  }
}

/** A record holding every field in `fields`, or a thrown `UnreadableAnswerError`. */
export function expectRecord<T>(value: unknown, what: string, fields: readonly string[]): T {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new UnreadableAnswerError(what);
  }
  const record = value as Record<string, unknown>;
  if (fields.some((field) => record[field] === undefined)) throw new UnreadableAnswerError(what);
  return value as T;
}

/** A list, or a thrown `UnreadableAnswerError`. An empty list is an answer: nothing is there. */
export function expectList<T>(value: unknown, what: string): T[] {
  if (!Array.isArray(value)) throw new UnreadableAnswerError(what);
  return value as T[];
}
